import React, { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, CheckCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/contact`, form);
      setSuccess(true);
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-ivory min-h-screen">
      {/* Header */}
      <section className="bg-navy py-24 px-6 text-center">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}
          className="font-lora text-xs tracking-[0.3em] uppercase text-gold mb-4">
          Contact
        </motion.p>
        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}
          className="font-lora text-4xl lg:text-5xl text-ivory font-normal">
          Get in Touch
        </motion.h1>
        <div className="w-12 h-px bg-gold mx-auto mt-6"></div>
      </section>

      <section className="py-24 px-6 lg:px-12">
        <div className="max-w-xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <p className="text-navy/60 font-serif leading-relaxed mb-10 text-center">
              For more complex advisory needs, collaborations, or inquiries. We will respond thoughtfully and promptly.
            </p>

            {success ? (
              <div className="text-center py-16 bg-white border border-soft-gray" data-testid="contact-success">
                <CheckCircle size={36} className="text-gold mx-auto mb-4" />
                <h2 className="font-lora text-2xl text-navy font-normal mb-2">Message Received</h2>
                <p className="text-navy/50 font-serif text-sm">Thank you for reaching out. We will be in touch shortly.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <div>
                  <label className="block font-lora text-xs tracking-[0.15em] uppercase text-navy/60 mb-2">Name</label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                    className="w-full border border-soft-gray bg-white px-4 py-3 text-navy font-serif text-sm focus:outline-none focus:border-navy transition-colors"
                    placeholder="Your full name"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label className="block font-lora text-xs tracking-[0.15em] uppercase text-navy/60 mb-2">Email</label>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                    className="w-full border border-soft-gray bg-white px-4 py-3 text-navy font-serif text-sm focus:outline-none focus:border-navy transition-colors"
                    placeholder="your@email.com"
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <label className="block font-lora text-xs tracking-[0.15em] uppercase text-navy/60 mb-2">Message</label>
                  <textarea
                    required
                    value={form.message}
                    onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
                    rows={5}
                    className="w-full border border-soft-gray bg-white px-4 py-3 text-navy font-serif text-sm focus:outline-none focus:border-navy transition-colors resize-none"
                    placeholder="Describe your inquiry..."
                    data-testid="contact-message-input"
                  />
                </div>
                {error && <p className="text-red-600 font-serif text-sm" data-testid="contact-error">{error}</p>}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-navy text-ivory py-3.5 text-sm tracking-[0.15em] uppercase hover:bg-navy/90 transition-all flex items-center justify-center gap-2 disabled:opacity-60"
                  data-testid="contact-submit-btn"
                >
                  {loading ? <><Loader2 size={15} className="animate-spin" /> Sending...</> : "Send Message"}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
}
